// Unified lifecycle "stage" vocabulary shared by relay.contacts and
// relay.companies. The DB CHECK constraints for both tables' `status` column
// mirror STAGES exactly (see
// supabase/migrations/20260601130000_relay_unified_contact_company_stage.sql).
//
// Keep STAGES, the CHECK constraints, and the generated database types in sync:
// changing the set here requires a migration that updates both constraints.

import type { BadgeProps } from "@/components/ui/badge";

export const STAGES = [
  "new",
  "contacted",
  "qualified",
  "active_customer",
  "wrapped",
  "inactive_customer",
  "lost",
] as const;

export type Stage = (typeof STAGES)[number];

export const DEFAULT_STAGE: Stage = "new";

/** Human-readable label for each stage (chips, dropdowns, badges). */
export const STAGE_LABELS: Record<Stage, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  active_customer: "Active customer",
  wrapped: "Wrapped",
  inactive_customer: "Inactive customer",
  lost: "Lost",
};

/** Tooltip / helper copy explaining what each stage means. */
export const STAGE_DEFINITIONS: Record<Stage, string> = {
  new: "Just added; no outreach yet.",
  contacted: "Reached out; awaiting a response.",
  qualified: "Confirmed fit or interest; worth pursuing.",
  active_customer: "Currently engaged in paid or active work.",
  wrapped: "Campaign or service delivered; engagement complete.",
  inactive_customer: "Past customer, not currently engaged.",
  lost: "Disqualified or fell through.",
};

/** Badge variant used when rendering a stage as a colored pill. */
export const STAGE_BADGE_VARIANT: Record<Stage, BadgeProps["variant"]> = {
  new: "steel",
  contacted: "coral",
  qualified: "gold",
  active_customer: "success",
  wrapped: "steel",
  inactive_customer: "secondary",
  lost: "destructive",
};

/** Safe label lookup for values that may not be a known stage (legacy rows). */
export function stageLabel(value: string): string {
  return (STAGE_LABELS as Record<string, string>)[value] ?? value;
}

/** Safe badge-variant lookup; unknown values fall back to a neutral pill. */
export function stageBadgeVariant(value: string): BadgeProps["variant"] {
  return (STAGE_BADGE_VARIANT as Record<string, BadgeProps["variant"]>)[value] ?? "outline";
}

/** Safe definition lookup for values that may not be a known stage. */
export function stageDefinition(value: string): string | undefined {
  return (STAGE_DEFINITIONS as Record<string, string>)[value];
}
